// Mirrors of the serde types in `stereo-core`. Keep in sync with Rust.

export interface DepthData {
  width: number;
  height: number;
  /** Row-major, 0..1, 1 = near. */
  data: Float32Array;
}

/** Compact depth for sequences: 0..65535, 65535 = near. */
export interface Depth16 {
  width: number;
  height: number;
  data: Uint16Array;
}

export interface RgbaData {
  width: number;
  height: number;
  data: Uint8ClampedArray | Uint8Array;
}

export type ViewMode = 'wall_eyed' | 'cross_eyed';

export interface StereoParams {
  eye_sep_mm: number;
  dpi: number;
  mu: number;
  pattern_width: number;
  mode: ViewMode;
  oversample: number;
  guide_dots: boolean;
  seed: number;
  pattern_shift_y?: number;
}

export type DotStyle = 'bw' | 'color' | 'palette';
export type Seamless = 'none' | 'mirror' | 'blend';

export type TextureSpec =
  | { kind: 'random_dots'; style: DotStyle; palette: string[]; density: number; dot_size: number }
  | { kind: 'noise'; scale: number; octaves: number; palette: string[] }
  | { kind: 'worley'; scale: number; palette: string[] }
  | { kind: 'stripes'; stripe_width: number; waviness: number; palette: string[] }
  | { kind: 'marble'; scale: number; turbulence: number; palette: string[] }
  | { kind: 'camo'; scale: number; palette: string[] }
  | { kind: 'mosaic'; scale: number; edge: number; palette: string[] }
  | { kind: 'bubbles'; radius: number; palette: string[] }
  | { kind: 'plasma'; scale: number; bands: number }
  | { kind: 'rings'; scale: number; rings: number; palette: string[] }
  | { kind: 'confetti'; size: number; density: number; palette: string[] }
  | { kind: 'stars'; density: number; palette: string[] }
  | { kind: 'image'; seamless: Seamless }
  | { kind: 'source'; strength: number };

export type TextureKind = TextureSpec['kind'];

export type DepthOp =
  | { op: 'normalize'; low?: number; high?: number }
  | { op: 'remap'; lo: number; hi: number }
  | { op: 'invert' }
  | { op: 'gamma'; gamma: number }
  | { op: 'curve'; points: [number, number][] }
  | { op: 'clamp'; near: number; far: number }
  | { op: 'range'; min: number; max: number }
  | { op: 'blur'; radius: number }
  | { op: 'bilateral'; radius: number; sigma_space: number; sigma_range: number }
  | { op: 'guided'; radius: number; eps: number }
  | { op: 'quantize'; levels: number }
  | { op: 'resize'; width: number; height: number };

export type DepthHeuristic = { kind: 'brightness' } | { kind: 'edges'; threshold: number };

export const DEFAULT_STEREO: StereoParams = {
  eye_sep_mm: 63.5,
  dpi: 96,
  mu: 1 / 3,
  pattern_width: 0,
  mode: 'wall_eyed',
  oversample: 4,
  guide_dots: true,
  seed: 1,
};

export const DEFAULT_PALETTE = ['#101830', '#286eaa', '#f0c85a', '#fafaf0'];
export const CAMO_PALETTE = ['#3c4628', '#6e7846', '#96825a', '#28231e'];
export const WOOD_PALETTE = ['#5c3418', '#a06838', '#d6aa70', '#8c562c'];
export const NIGHT_PALETTE = ['#060816', '#221846', '#143c60', '#fffae6'];

/** Ready-made palettes (the first colour is the background for dots, bubbles, confetti). */
export const NAMED_PALETTES: Record<string, string[]> = {
  ocean: ['#03203c', '#0b5d8f', '#2aa3c9', '#9ee6f2', '#f4fbff'],
  sunset: ['#2b1036', '#7a1f4d', '#d6455d', '#f59b54', '#ffe29a'],
  forest: ['#14261a', '#2f5d34', '#6b9a3e', '#c2c96a', '#efe9b8'],
  candy: ['#fff0f6', '#ff5fa2', '#7b6cff', '#3fd3c4', '#ffd23f'],
  fire: ['#1a0500', '#6e1400', '#d33a00', '#ff9a1f', '#fff2a8'],
  neon: ['#08060f', '#ff2bd6', '#2bf5ff', '#9dff2b', '#fff36b'],
  pastel: ['#f8f4ec', '#f4b6c2', '#b8d8e8', '#c8e6b8', '#f8dfa0'],
  earth: ['#2a1f14', '#6b4a2b', '#a87d4f', '#d8c39a', '#7d8f5a'],
  gold: ['#1c1206', '#5a3c0a', '#b8860b', '#e8c26a', '#fff4cc'],
  ice: ['#0e1a2b', '#3d6b8f', '#8fc1e0', '#d6efff', '#ffffff'],
  mono: ['#000000', '#555555', '#aaaaaa', '#ffffff'],
  rainbow: ['#e6261f', '#eb7532', '#f7d038', '#a3e048', '#49da9a', '#34bbe6', '#4355db', '#d23be7'],
};

export function defaultTexture(kind: TextureKind): TextureSpec {
  switch (kind) {
    case 'random_dots':
      return { kind, style: 'bw', palette: [...DEFAULT_PALETTE], density: 0.5, dot_size: 1 };
    case 'noise':
      return { kind, scale: 24, octaves: 4, palette: [...DEFAULT_PALETTE] };
    case 'worley':
      return { kind, scale: 20, palette: [...DEFAULT_PALETTE] };
    case 'stripes':
      return { kind, stripe_width: 6, waviness: 0.5, palette: [...DEFAULT_PALETTE] };
    case 'marble':
      return { kind, scale: 24, turbulence: 2, palette: [...DEFAULT_PALETTE] };
    case 'camo':
      return { kind, scale: 20, palette: [...CAMO_PALETTE] };
    case 'mosaic':
      return { kind, scale: 14, edge: 1.5, palette: [...NAMED_PALETTES.candy] };
    case 'bubbles':
      return { kind, radius: 6, palette: [...NAMED_PALETTES.ocean] };
    case 'plasma':
      return { kind, scale: 24, bands: 1.2 };
    case 'rings':
      return { kind, scale: 26, rings: 12, palette: [...WOOD_PALETTE] };
    case 'confetti':
      return { kind, size: 7, density: 0.6, palette: [...NAMED_PALETTES.candy] };
    case 'stars':
      return { kind, density: 0.3, palette: [...NIGHT_PALETTE] };
    case 'image':
      return { kind, seamless: 'blend' };
    case 'source':
      return { kind, strength: 0.75 };
  }
}

/** Separation of the far plane in output pixels (texture tile width). */
export function maxSeparationPx(p: StereoParams): number {
  const e = p.pattern_width > 0 ? 2 * p.pattern_width : (p.eye_sep_mm / 25.4) * p.dpi;
  return Math.max(1, Math.round(e / 2));
}

export type PatternMotion = { kind: 'static' } | { kind: 'boiling' } | { kind: 'scroll'; speed: number };

export type TemporalFilter =
  | { kind: 'none' }
  | { kind: 'ema'; alpha: number; threshold: number }
  | { kind: 'median'; radius: number; threshold: number };

export type ProceduralAnim =
  | { kind: 'waves'; amplitude: number; wavelength: number; direction: number; cycles: number }
  | { kind: 'noise'; amplitude: number; scale: number; cycles: number; seed: number }
  | { kind: 'ripple'; amplitude: number; cx: number; cy: number; wavelength: number; cycles: number; decay: number };

export function toDepth16(d: DepthData): Depth16 {
  const out = new Uint16Array(d.data.length);
  for (let i = 0; i < out.length; i++) out[i] = Math.round(Math.min(1, Math.max(0, d.data[i])) * 65535);
  return { width: d.width, height: d.height, data: out };
}

export function fromDepth16(d: Depth16): DepthData {
  const out = new Float32Array(d.data.length);
  for (let i = 0; i < out.length; i++) out[i] = d.data[i] / 65535;
  return { width: d.width, height: d.height, data: out };
}
